import React from "react";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import { TextField, CircularProgress } from "@mui/material";
import Autocomplete, { autocompleteClasses } from "@mui/material/Autocomplete";
import { VariableSizeList } from "react-window";
import useMediaQuery from "@mui/material/useMediaQuery";
import ListSubheader from "@mui/material/ListSubheader";
import { useTheme } from "@mui/styles";
import { Typography } from "@mui/material";
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import { Box, Chip } from "@mui/material";
import Api from "../../services/api";

const LISTBOX_PADDING = 8;

function renderRow(props) {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: style.top + LISTBOX_PADDING,
    wordWrap: "break-word",
  };

  if (dataSet.hasOwnProperty('group')) {
    return (
      <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
        {dataSet.group}
      </ListSubheader>
    );
  }

  const option = dataSet[1];
  let label = '';
  if (option.inputValue) {
    label = option.name; // "Adicionar: ..."
  } else if (option.parentname) {
    label = option.parentname;
  } else if (option.name && option.email) {
    label = option.name + ' (' + option.email + ')';
  } else {
    label = option.name || '';
  }

  return (
    <Typography component="li" {...dataSet[0]} noWrap style={inlineStyle}>
      {label}
    </Typography>
  );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData = [];
  children.forEach((item) => {
    itemData.push(item);
    itemData.push(...(item.children || []));
  });

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });

  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child) => child.hasOwnProperty('group') ? 48 : itemSize;

  const getHeight = () => {
    if (itemCount > 8) return 8 * itemSize;
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

ListboxComponent.propTypes = { children: PropTypes.node };

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': { padding: 0, margin: 0 },
  },
});

const useStyles = makeStyles(() => ({
  listbox: { "& ul": { padding: 0, margin: 0 } },
}));

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const hasAtSign = (value) => (value + '').includes('@');

export default function SimpleAutoCompleteMultipleObservant(props) {
  const {
    label,
    options,
    stateName,
    changeSelect,
    selected,
    required,
    disabled = false,
    freeSolo = false,
    urlapi = null,
    minDigits = 3,
  } = props;

  const defaultvalue = [];
  const classes = useStyles();

  const [opt, setOption] = React.useState(selected ? selected : defaultvalue);
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [inputText, setInputText] = React.useState('');
  const [emailError, setEmailError] = React.useState('');
  const previousController = React.useRef();
  const debounceTimer = React.useRef();

  React.useEffect(() => {
    setOption(selected ? selected : defaultvalue);
  }, [selected]);

  const fetchUsers = async (searchTerm) => {
    if (!urlapi) return;
    if (previousController.current) {
      previousController.current.abort();
    }
    const controller = new AbortController();
    previousController.current = controller;
    await Api.get(urlapi + '/' + searchTerm, { signal: controller.signal })
      .then((response) => {
        setItems(response.data.data || []);
        setLoading(false);
      })
      .catch((err) => {
        if (err && (err.name === 'CanceledError' || err.name === 'AbortError')) return;
        setItems([]);
        setLoading(false);
      });
  };

  const onInputChange = (event, value, reason) => {
    setInputText(value);
    setEmailError('');
    if (reason !== 'input') return;

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    if (!urlapi || (value + '').length < minDigits) {
      setItems([]);
      setLoading(false);
      return;
    }

    // Show loading immediately while debounce is pending
    setLoading(true);
    setOpen(true);

    debounceTimer.current = setTimeout(() => {
      fetchUsers(value);
    }, 700);
  };

  const handleChange = (event, value) => {
    if (value == null) value = [];
    const normalized = [];
    let error = '';

    value.forEach(item => {
      if (typeof item === 'string') {
        if (isValidEmail(item)) {
          normalized.push({ id: 0, name: item, email: item });
        } else if (hasAtSign(item)) {
          error = `"${item}" não é um e-mail válido`;
        }
        // strings without @ are ignored silently
      } else if (item && item.inputValue) {
        if (isValidEmail(item.inputValue)) {
          normalized.push({ id: 0, name: item.inputValue, email: item.inputValue });
        } else {
          error = `"${item.inputValue}" não é um e-mail válido`;
        }
      } else {
        normalized.push(item);
      }
    });

    setEmailError(error);
    setOption(normalized);
    changeSelect(stateName, normalized);
    if (!error) setOpen(false);
  };

  // Server already filtered — show "Add" option as soon as @ is typed
  const filterOptionsHandler = (opts, params) => {
    const { inputValue } = params;
    if (freeSolo && inputValue !== '' && hasAtSign(inputValue) && opts.length === 0) {
      return [{
        id: 0,
        name: `Adicionar: "${inputValue}"`,
        email: inputValue,
        inputValue: inputValue,
      }];
    }
    return opts;
  };

  const getOptionLabel = (option) => {
    if (typeof option === 'string') return option;
    if (option.inputValue) return option.inputValue;
    if (option.name && option.email && option.id !== 0) return option.name + ' (' + option.email + ')';
    return option.parentname || option.name || option.email || '';
  };

  return (
    <Autocomplete
      disabled={disabled}
      multiple
      limitTags={2}
      disableCloseOnSelect
      id="combo-box-observant"
      PopperComponent={StyledPopper}
      filterSelectedOptions={false}
      options={items}
      disableListWrap
      classes={classes}
      value={opt}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      ListboxComponent={ListboxComponent}
      freeSolo={freeSolo}
      filterOptions={filterOptionsHandler}
      loading={loading}
      loadingText="Pesquisando..."
      noOptionsText={
        (inputText + '').length < minDigits
          ? `Digite pelo menos ${minDigits} caracteres para pesquisar`
          : "Nenhum resultado encontrado"
      }
      onInputChange={onInputChange}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, value) => {
        if (value.id === 0 && option.id === 0) return option.email === value.email;
        return option.id === value.id;
      }}
      onChange={handleChange}
      renderOption={(props, option) => [props, option]}
      style={{
        fullWidth: true,
        display: "table",
        tableLayout: "fixed",
        width: "100%",
        maxWidth: "100%",
      }}
      renderTags={(value, getTagProps, ownerState) => {
        const getLabel = (option) =>
          option.id === 0 && option.email ? option.email : option.parentname || option.name || '';

        if (!ownerState.focused) {
          const tagsToDisplay = value.slice(0, 2);
          const numRemaining = value.length - 2;
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {tagsToDisplay.map((option, index) => (
                <Chip key={index} label={getLabel(option)} {...getTagProps({ index })} />
              ))}
              {numRemaining > 0 && (
                <Typography sx={{ alignSelf: 'center', mx: 0.5 }}>+{numRemaining}</Typography>
              )}
            </Box>
          );
        }
        return (
          <Box sx={{ maxHeight: '200px', overflow: 'auto', display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {value.map((option, index) => (
              <Chip key={index} label={getLabel(option)} {...getTagProps({ index })} />
            ))}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          placeholder={`${label} - digite para pesquisar`}
          fullWidth
          required={required}
          error={!!emailError}
          helperText={emailError || ''}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

SimpleAutoCompleteMultipleObservant.propTypes = {
  label: PropTypes.string,
  options: PropTypes.array,
  stateName: PropTypes.string,
  changeSelect: PropTypes.func,
  selected: PropTypes.array,
  freeSolo: PropTypes.bool,
  urlapi: PropTypes.string,
  minDigits: PropTypes.number,
};
